import React from "react";

import {
  Box,
  Center,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text
} from "@chakra-ui/react";

import { BankReconCard } from "@/components/features/settings/BankReconCard";
import { ChooseBusiness } from "@/components/features/settings/ChooseBusiness";
import { ChooseFetchDuration } from "@/components/features/settings/ChooseFetchDuration";
import { IntegrationCard } from "@/components/features/settings/IntegrationCard";
import { IntegrationDetails } from "@/components/features/settings/IntegrationDetails";
import { useCompanyIntegrations } from "@/hooks/features/useCompanyIntegrations";
import {
  bankReconPlatforms,
  commsPlatforms,
  emailPlatforms
} from "@/util/helpers";

export interface ClientPractiseConnectionsProps {}

export const ClientPractiseConnections: React.FC<
  ClientPractiseConnectionsProps
> = () => {
  const data = useCompanyIntegrations();
  return (
    <Box mt={8} rounded={16} px={10} py={10} bg="white">
      <Box>
        <Heading fontSize={16} mb={4} fontWeight={600}>
          Connections
        </Heading>
        <Text fontSize={14}>Connect your favourite third party apps</Text>
      </Box>

      <Box mt={14}>
        <Text fontWeight={600} mb={4}>
          Accounting Platform
        </Text>

        {data.isFetchingStatus && (
          <Center>
            <Spinner color="primary.500" />
          </Center>
        )}
        {!data.isFetchingStatus && (
          <Stack spacing={17}>
            {data.accountingIntegrations.map((integration) => (
              <IntegrationDetails
                key={integration.platform.name}
                integration={integration}
                {...data}
              />
            ))}
          </Stack>
        )}

        {data.chooseBusinessDisclosure.isOpen && (
          <ChooseBusiness
            clientId={data.clientId}
            disclosure={data.chooseBusinessDisclosure}
            isLinking={data.isLinkingSageBusiness}
            linkBusiness={data.handleLinkBusiness}
            handleDisconnect={data.HandleDisconnect}
          />
        )}
      </Box>

      <Box mt={14}>
        <Text fontWeight={600} mb={4}>
          Bank Reconciliation
        </Text>

        {!data.isFetchingBankConnections && (
          <SimpleGrid spacing={4} columns={[1, 1, 1, 2]}>
            {Object.keys(bankReconPlatforms).map((key) => {
              const platform = bankReconPlatforms[key];
              return (
                <BankReconCard
                  key={key}
                  connections={data.bankConnections}
                  platform={platform}
                />
              );
            })}
          </SimpleGrid>
        )}

        {data.chooseFetchDurationDisclosure.isOpen && (
          <ChooseFetchDuration
            clientId={data.clientId}
            disclosure={data.chooseFetchDurationDisclosure}
          />
        )}
      </Box>

      <Box mt={14}>
        <Text fontWeight={600} mb={4}>
          Communication
        </Text>

        <SimpleGrid spacing={17} columns={[1, 1, 2, 2, 3, 4]}>
          {Object.keys(commsPlatforms).map((key) => {
            const platform = commsPlatforms[key];
            return <IntegrationCard key={key} platform={platform} />;
          })}
        </SimpleGrid>
      </Box>

      <Box mt={14}>
        <Text fontWeight={600} mb={4}>
          Email
        </Text>

        <SimpleGrid spacing={17} columns={[1, 1, 2, 2, 3, 4]}>
          {Object.keys(emailPlatforms).map((key) => {
            const platform = emailPlatforms[key];
            return <IntegrationCard key={key} platform={platform} />;
          })}
        </SimpleGrid>
      </Box>
    </Box>
  );
};
